// src/components/home/DataFlow.js
// Industrial data flow diagram:
// machines -> edge collection -> databases & AI -> insights & actions.
import React from 'react';
import { useTranslation } from 'react-i18next';
import SectionTitle from '../SectionTitle';

const STEP_ICONS = [
    // Machines & sensors
    <svg key="i0" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
    </svg>,
    // Edge data collection
    <svg key="i1" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />
    </svg>,
    // Databases & AI processing
    <svg key="i2" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l1.41-.513M5.106 17.785l1.15-.964m11.49-9.642l1.149-.964M7.501 19.795l.75-1.3m7.5-12.99l.75-1.3m-6.063 16.658l.26-1.477m2.605-14.772l.26-1.477m0 17.726l-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205L12 12m6.894 5.785l-1.149-.964M6.256 7.178l-1.15-.964m15.352 8.864l-1.41-.513M4.954 9.435l-1.41-.514M12.002 12l-3.75 6.495" />
    </svg>,
    // Insights & actions
    <svg key="i3" className="w-10 h-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
];

const Arrow = () => (
    <div className="flex items-center justify-center text-primary" aria-hidden="true">
        {/* right arrow on desktop, down arrow on mobile */}
        <svg className="w-8 h-8 hidden lg:block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
        <svg className="w-8 h-8 lg:hidden" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" /></svg>
    </div>
);

const DataFlow = () => {
    const { t } = useTranslation();
    const steps = t('home.dataflow.steps', { returnObjects: true }) || [];

    return (
        <div className="bg-white py-12">
            <section data-aos="fade-up">
                <SectionTitle title={t('home.dataflow.title')} subtitle={t('home.dataflow.subtitle')} />

                <div className="max-w-7xl mx-auto px-4 sm:px-8 mt-8">
                    <div className="flex flex-col lg:flex-row items-stretch justify-center gap-2">
                        {Array.isArray(steps) && steps.map((step, i) => (
                            <React.Fragment key={i}>
                                <div
                                    className="flex-1 bg-gray-50 border border-gray-200 rounded-xl p-6 text-center flex flex-col items-center max-w-md mx-auto lg:mx-0 lg:max-w-none"
                                    data-aos="fade-up"
                                    data-aos-delay={i * 150}
                                >
                                    <div className="text-primary mb-3">{STEP_ICONS[i]}</div>
                                    <div className="text-xs font-bold text-accent mb-2">{`0${i + 1}`}</div>
                                    <h3 className="font-bold text-lg text-gray-800 mb-2">{step.title}</h3>
                                    <p className="text-sm text-gray-600">{step.desc}</p>
                                </div>
                                {i < steps.length - 1 && <Arrow />}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default DataFlow;
